import {
	Box,
	Button,
	ButtonGroup,
	Chip,
	Paper,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
	Typography,
} from "@mui/material";
import { useState } from "react";
import { ISinisterList } from "../../../shared/services/api/sinister/SinisterService";
import { SinisterCaseStatus } from "../../../shared/services/api/sinister/SinisterNegotiationService";
import { globalMaskDocument } from "../../../shared/utils";
import { formatScaleValueBR } from "../../../shared/utils/formatScaleValue";
import { HtmlTooltip } from "../../../infra/components/tooltipsCustom/tooltipsCuston";
import { SinisterSector } from "../constants/sectorOptions";
import { STAGE_OPTIONS_BY_SECTOR, SinisterStage, getStageLabel } from "../constants/stageOptions";
import { CreditRecoveryActionsIndicator } from "./CreditRecoveryActionsIndicator";
import { CreditRecoveryDeadlineIndicator } from "./CreditRecoveryDeadlineIndicator";
import { CreditRecoveryFollowUpButton } from "./CreditRecoveryFollowUpButton";
import { CreditRecoveryRowMenu } from "./CreditRecoveryRowMenu";
import { CreditRecoveryStatusCell } from "./CreditRecoveryStatusCell";

interface Props {
	rows: ISinisterList[];
	totalCount: number;
	page: number;
	rowsPerPage: number;
	selectedSector: SinisterSector;
	stageFilter: SinisterStage | "all";
	onStageFilterChange: (stage: SinisterStage | "all") => void;
	onPageChange: (newPage: number) => void;
	onRowsPerPageChange: (value: number) => void;
	onChangeSector: (sinisterId: string, sector: SinisterSector) => void;
	onChangeStage: (sinisterId: string, stage: SinisterStage) => void;
	onStatusUpdated: () => void;
	onResponsibleUpdated: () => void;
}

const TABLE_COLUMN_COUNT = 11;

export const TableCreditRecovery = ({
	rows,
	totalCount,
	page,
	rowsPerPage,
	selectedSector,
	stageFilter,
	onStageFilterChange,
	onPageChange,
	onRowsPerPageChange,
	onChangeSector,
	onChangeStage,
	onStatusUpdated,
	onResponsibleUpdated,
}: Props) => {
	const [actionsRefreshToken, setActionsRefreshToken] = useState<Record<string, number>>({});
	const stageOptionsForSector = STAGE_OPTIONS_BY_SECTOR[selectedSector];

	return (
		<TableContainer
			component={Paper}
			variant="elevation"
			sx={{ mt: 1, width: "96%", px: { xs: 1, sm: 3 }, py: { xs: 2, sm: 3 }, ml: 1, borderRadius: 2, overflow: "hidden" }}
		>
			<Box mb={2}>
				<ButtonGroup size="small" disableElevation aria-label="Filtro por etapa">
					<Button variant={stageFilter === "all" ? "contained" : "outlined"} onClick={() => onStageFilterChange("all")}>
						Todos
					</Button>
					{stageOptionsForSector.map((option) => (
						<Button
							key={option.value}
							variant={stageFilter === option.value ? "contained" : "outlined"}
							onClick={() => onStageFilterChange(option.value)}
						>
							{option.label}
						</Button>
					))}
				</ButtonGroup>
			</Box>

			<Table size="small" stickyHeader>
				<TableHead>
					<TableRow sx={{ backgroundColor: "grey.100" }}>
						{["Nome", "Parceiro", "Status", "Etapa", "Responsável", "Prazo", "Valor LMI", "Saldo a recuperar", "Ações", "Follow Up", "Opções"].map(
							(header, i) => (
								<TableCell key={i} align={i <= 1 ? "left" : "center"}>
									<Typography variant="button">{header}</Typography>
								</TableCell>
							)
						)}
					</TableRow>
				</TableHead>

				<TableBody>
					{rows.length === 0 ? (
						<TableRow>
							<TableCell colSpan={TABLE_COLUMN_COUNT} align="center" sx={{ py: 6 }}>
								<Typography variant="body2" color="text.secondary">
									Nenhum sinistro encontrado para os filtros aplicados.
								</Typography>
							</TableCell>
						</TableRow>
					) : (
						rows.map((row) => {
						const sector = row.setor ?? "cobranca";
						const stage = row.etapa;
						const deadlineDate = row.deadlineDate ?? null;
						const deadlineDatePos = row.deadlineDatePos ?? null;
						// Desocupação usa o prazo da etapa (deadlineDatePos); Pré Sinistro e demais seguem com deadlineDate.
						const stageDeadlineDate = stage === "post_sinister" ? deadlineDatePos : deadlineDate;
						return (
							<TableRow key={row.id} hover>
								<TableCell>
									<HtmlTooltip
										title={
											<Box>
												<Typography variant="subtitle2" fontWeight="bold">
													{row.proposal?.name}
												</Typography>
											</Box>
										}
										arrow
										placement="top"
									>
										<Box>
											<Typography
												variant="body2"
												sx={{ maxWidth: "26ch", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
											>
												{row.proposal?.name || "Sem nome"}
											</Typography>
											<Typography variant="caption" color="text.secondary">
												{globalMaskDocument(row.proposal?.document)}
											</Typography>
										</Box>
									</HtmlTooltip>
								</TableCell>

								<TableCell>
									<HtmlTooltip
										title={
											<Box>
												<Typography variant="subtitle2" fontWeight="bold">
													{row.client?.name}
												</Typography>
											</Box>
										}
										arrow
										placement="top"
									>
										<Box>
											<Typography
												variant="body2"
												sx={{ maxWidth: "26ch", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
											>
												{row.client?.name}
											</Typography>
											<Typography variant="caption" color="text.secondary">
												{globalMaskDocument(row.client?.document)}
											</Typography>
										</Box>
									</HtmlTooltip>
								</TableCell>

								<TableCell align="center">
									<CreditRecoveryStatusCell sinisterId={row.id} caseStatus={row.caseStatus} />
								</TableCell>

								<TableCell align="center">
									{stage ? (
										<Chip size="small" variant="outlined" label={getStageLabel(stage)} />
									) : (
										<Typography variant="body2" color="text.secondary">
											—
										</Typography>
									)}
								</TableCell>

								<TableCell align="center">
									{row.collectionResponsibleName ? (
										<Typography variant="body2">{row.collectionResponsibleName}</Typography>
									) : (
										<Typography variant="body2" color="text.secondary">
											Não atribuído
										</Typography>
									)}
								</TableCell>

								<TableCell align="center">
									<CreditRecoveryDeadlineIndicator deadlineDate={stageDeadlineDate} caseStatus={row.caseStatus} stage={stage} totalDays={row.deadlineTotalDays} />
								</TableCell>

								<TableCell align="center">
									<Typography variant="body2">{formatScaleValueBR(row.proposal?.lmiAmount)}</Typography>
								</TableCell>

								<TableCell align="center">
									<Typography variant="body2" fontWeight={600}>
										{formatScaleValueBR(String(row.proposalViewLmiSaldo?.valor ?? row.proposal?.lmiBalance ?? ""))}
									</Typography>
								</TableCell>

								<TableCell align="center">
									<CreditRecoveryActionsIndicator sinisterId={row.id} refreshToken={actionsRefreshToken[row.id]} />
								</TableCell>

								<TableCell align="center">
									<CreditRecoveryFollowUpButton sinisterId={row.id} status={row.status} />
								</TableCell>

								<TableCell align="center">
									<CreditRecoveryRowMenu
										sinisterId={row.id}
										sector={sector}
										stage={stage}
										caseStatus={row.caseStatus as SinisterCaseStatus | undefined}
										busyProperty={row.busyProperty}
										deadlineDate={deadlineDate}
										collectionResponsibleId={row.collectionResponsibleId}
										collectionResponsibleName={row.collectionResponsibleName}
										onChangeSector={(newSector) => onChangeSector(row.id, newSector)}
										onChangeStage={(newStage) => onChangeStage(row.id, newStage)}
										onStatusUpdated={onStatusUpdated}
										onResponsibleUpdated={onResponsibleUpdated}
										onInteractionRegistered={() =>
											setActionsRefreshToken((prev) => ({ ...prev, [row.id]: (prev[row.id] ?? 0) + 1 }))
										}
									/>
								</TableCell>
							</TableRow>
						);
						})
					)}
				</TableBody>
			</Table>

			<Box display="flex" justifyContent="flex-end" alignItems="center" mt={2} pr={2}>
				<TablePagination
					component="div"
					count={totalCount}
					page={page}
					rowsPerPage={rowsPerPage}
					onPageChange={(_, newPage) => onPageChange(newPage)}
					onRowsPerPageChange={(e) => onRowsPerPageChange(parseInt(e.target.value, 10))}
					rowsPerPageOptions={[10, 25, 50]}
					labelRowsPerPage="Linhas por página:"
					labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count} registros`}
				/>
			</Box>
		</TableContainer>
	);
};
